/**
 * KYC Version Diff View Component
 * TASK-202: Visual diff for KYC profile changes
 *
 * Displays a color-coded view of changes between versions:
 * - Green: Added fields
 * - Blue: Modified fields (with old → new values)
 * - Red: Removed fields
 */

import React from 'react';
import { Plus, Edit, Trash2, Info, ChevronDown, ChevronRight } from 'lucide-react';
import type { VersionDiff, DiffItem } from '../../../../types/kyc.types';
import { formatValue } from '../../../../utils/formatters';

interface KYCVersionDiffViewProps {
  diff: VersionDiff | null;
  versionNumber?: number;
}

const KYCVersionDiffView: React.FC<KYCVersionDiffViewProps> = ({
  diff,
  versionNumber
}) => {
  const [expandedSections, setExpandedSections] = React.useState<Set<string>>(
    new Set(['modified', 'added', 'removed'])
  );

  if (!diff) {
    return null;
  }

  const formatPath = (path: string): string => {
    // Convert "personalInfo.firstName" to "Personal Info → First Name"
    return path
      .split('.')
      .map(part => {
        // Handle array indices: "wealthSources[0]" -> "Wealth Sources #1"
        const arrayMatch = part.match(/^(.+)\[(\d+)\]$/);
        if (arrayMatch) {
          const name = arrayMatch[1]
            .replace(/([A-Z])/g, ' $1')
            .trim()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
          const index = parseInt(arrayMatch[1]) + 1;
          return `${name} #${index}`;
        }

        // Regular field name
        return part
          .replace(/([A-Z])/g, ' $1')
          .trim()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      })
      .join(' → ');
  };

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const renderDiffSection = (
    title: string,
    icon: React.ReactNode,
    items: DiffItem[],
    color: 'green' | 'blue' | 'red',
    sectionKey: string
  ) => {
    if (items.length === 0) return null;

    const isExpanded = expandedSections.has(sectionKey);
    const colorClasses = {
      green: {
        bg: 'bg-green-50',
        border: 'border-green-200',
        text: 'text-green-900',
        badge: 'bg-green-100 text-green-800'
      },
      blue: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-900',
        badge: 'bg-blue-100 text-blue-800'
      },
      red: {
        bg: 'bg-red-50',
        border: 'border-red-200',
        text: 'text-red-900',
        badge: 'bg-red-100 text-red-800'
      }
    };

    const colors = colorClasses[color];

    return (
      <div className={`${colors.bg} rounded-lg p-4 border ${colors.border}`}>
        <button
          onClick={() => toggleSection(sectionKey)}
          className="w-full flex items-center justify-between cursor-pointer hover:opacity-80"
        >
          <h4 className={`font-semibold ${colors.text} flex items-center gap-2`}>
            {icon}
            {title} ({items.length})
          </h4>
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-600" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-600" />
          )}
        </button>

        {isExpanded && (
          <div className="space-y-3 mt-3">
            {items.map((item, idx) => (
              <div key={idx} className="bg-white rounded p-3 shadow-sm">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  {formatPath(item.path)}
                </p>

                {/* Modified fields: show old → new */}
                {item.oldValue !== undefined && item.newValue !== undefined && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <span className="text-xs text-red-600 font-medium">Old Value:</span>
                      <pre className="text-sm text-gray-900 line-through mt-1 whitespace-pre-wrap break-words">
                        {formatValue(item.oldValue)}
                      </pre>
                    </div>
                    <div>
                      <span className="text-xs text-green-600 font-medium">New Value:</span>
                      <pre className="text-sm text-gray-900 font-semibold mt-1 whitespace-pre-wrap break-words">
                        {formatValue(item.newValue)}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Added fields: show new value */}
                {item.value !== undefined && item.oldValue === undefined && (
                  <div>
                    <span className="text-xs text-green-600 font-medium">Value:</span>
                    <pre className="text-sm text-gray-900 mt-1 whitespace-pre-wrap break-words">
                      {formatValue(item.value)}
                    </pre>
                  </div>
                )}

                {/* Removed fields: show old value */}
                {item.oldValue !== undefined && item.newValue === undefined && (
                  <div>
                    <span className="text-xs text-red-600 font-medium">Removed Value:</span>
                    <pre className="text-sm text-gray-500 line-through mt-1 whitespace-pre-wrap break-words">
                      {formatValue(item.oldValue)}
                    </pre>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const hasChanges = diff.added.length > 0 || diff.modified.length > 0 || diff.removed.length > 0;

  if (!hasChanges) {
    return (
      <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
        <div className="text-center">
          <Info className="w-8 h-8 mx-auto mb-2 text-gray-400" />
          <p className="text-gray-600">
            {versionNumber
              ? `No changes in version ${versionNumber}`
              : 'No changes detected'}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            This version has the same data as the previous version
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Header */}
      <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">
          Change Summary
          {versionNumber && ` - Version ${versionNumber}`}
        </h3>
        <div className="flex gap-4 text-sm">
          {diff.modified.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-blue-500"></span>
              <span className="text-gray-700">
                {diff.modified.length} modified
              </span>
            </div>
          )}
          {diff.added.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-green-500"></span>
              <span className="text-gray-700">
                {diff.added.length} added
              </span>
            </div>
          )}
          {diff.removed.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-red-500"></span>
              <span className="text-gray-700">
                {diff.removed.length} removed
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Modified Fields */}
      {renderDiffSection(
        'Modified Fields',
        <Edit className="w-4 h-4" />,
        diff.modified,
        'blue',
        'modified'
      )}

      {/* Added Fields */}
      {renderDiffSection(
        'Added Fields',
        <Plus className="w-4 h-4" />,
        diff.added,
        'green',
        'added'
      )}

      {/* Removed Fields */}
      {renderDiffSection(
        'Removed Fields',
        <Trash2 className="w-4 h-4" />,
        diff.removed,
        'red',
        'removed'
      )}
    </div>
  );
};

export default KYCVersionDiffView;
